'use client';
import React, { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface VehicleTier {
  size: string;
  tag: string;
  highlight?: boolean;
  interior: string;
  exterior: string;
  full: string;
}

const vehicleTiers: VehicleTier[] = [
  {
    size: 'Coupe',
    tag: 'Compact',
    interior: '$75',
    exterior: '$50',
    full: '$110',
  },
  {
    size: 'Mid-Size',
    tag: 'Most Popular',
    highlight: true,
    interior: '$80',
    exterior: '$60',
    full: '$125',
  },
  {
    size: 'Full Size',
    tag: 'SUV / Truck',
    interior: '$90',
    exterior: '$70',
    full: '$140',
  },
];

const services = [
  { key: 'interior' as const, label: 'Interior Detail', icon: 'SparklesIcon' as const, duration: '1 hr' },
  { key: 'exterior' as const, label: 'Exterior Detail', icon: 'ShieldCheckIcon' as const, duration: '30 min' },
  { key: 'full' as const, label: 'Full Detail', icon: 'WrenchScrewdriverIcon' as const, duration: '1.5–2 hrs' },
];

const addOns = [
  { name: 'Odor Elimination', price: '+$30' },
  { name: 'Pet Hair Removal', price: '+$40' },
  { name: 'Headlight Restoration', price: '+$50' },
  { name: 'Engine Bay Clean', price: '+$60' },
  { name: 'Seat Shampoo', price: '+$60' },
  { name: 'Ceramic Coating', price: 'Call for Quote' },
  { name: 'Paint Correction', price: 'Call for Quote' },
];

export default function ServicesSection({ onBook }: { onBook: () => void }) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    el.classList.add('hidden-initial');
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.remove('hidden-initial');
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="pricing" ref={sectionRef} className="reveal py-20 md:py-28">
      {/* Section header */}
      <div className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="mono text-[10px] tracking-widest uppercase mb-3" style={{ color: '#2D5F9A' }}>
            Services & Pricing
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-off-white leading-none">
            Choose Your Service<br />
            <span style={{ color: '#A8A9AD', fontWeight: 300 }}>We Come to You.</span>
          </h2>
        </div>
        <p className="text-sm max-w-xs" style={{ color: 'rgba(168,169,173,0.6)' }}>
          All services are fully mobile — we bring everything needed to your location.
        </p>
      </div>

      {/* Service type legend */}
      <div className="flex flex-wrap gap-4 mb-8">
        {services.map((svc) => (
          <div key={svc.key} className="flex items-center gap-2">
            <Icon name={svc.icon} size={15} variant="outline" className="text-sapphire" />
            <span className="text-sm font-medium" style={{ color: 'rgba(212,212,216,0.8)' }}>{svc.label}</span>
            <span className="mono text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full" style={{ background: 'rgba(45,95,154,0.12)', color: 'rgba(45,95,154,0.8)', border: '1px solid rgba(45,95,154,0.2)' }}>{svc.duration}</span>
          </div>
        ))}
      </div>

      {/* Pricing cards by vehicle size */}
      <div className="grid md:grid-cols-3 gap-4 md:gap-6 mb-12">
        {vehicleTiers.map((tier) => (
          <div
            key={tier.size}
            className="service-card rounded-2xl p-7 flex flex-col"
            style={{
              background: tier.highlight
                ? 'linear-gradient(160deg, rgba(45,95,154,0.18) 0%, rgba(45,95,154,0.06) 100%)'
                : 'rgba(255,255,255,0.03)',
              border: `1px solid ${tier.highlight ? 'rgba(45,95,154,0.35)' : 'rgba(212,212,216,0.09)'}`,
            }}
          >
            {/* Tag + size */}
            <div className="flex items-center justify-between mb-5">
              <span
                className="mono text-[9px] font-semibold tracking-widest uppercase px-3 py-1 rounded-full"
                style={{
                  background: tier.highlight ? 'rgba(45,95,154,0.25)' : 'rgba(168,169,173,0.08)',
                  color: tier.highlight ? '#2D5F9A' : 'rgba(168,169,173,0.5)',
                  border: `1px solid ${tier.highlight ? 'rgba(45,95,154,0.3)' : 'rgba(168,169,173,0.12)'}`,
                }}
              >
                {tier.tag}
              </span>
            </div>

            <h3 className="text-2xl font-bold text-off-white mb-6">{tier.size}</h3>

            {/* Service rows */}
            <div className="flex flex-col gap-3 flex-grow mb-8">
              {services.map((svc) => (
                <div
                  key={svc.key}
                  className="flex items-center justify-between px-4 py-3 rounded-xl"
                  style={{
                    background: tier.highlight ? 'rgba(45,95,154,0.1)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${tier.highlight ? 'rgba(45,95,154,0.18)' : 'rgba(212,212,216,0.07)'}`,
                  }}
                >
                  <div className="flex items-center gap-2.5">
                    <Icon
                      name={svc.icon}
                      size={14}
                      variant="outline"
                      className={tier.highlight ? 'text-sapphire shrink-0' : 'text-aluminum shrink-0'}
                    />
                    <span className="text-sm" style={{ color: 'rgba(212,212,216,0.75)' }}>{svc.label}</span>
                  </div>
                  <span
                    className="text-base font-bold"
                    style={{ color: tier.highlight ? '#2D5F9A' : '#D4D4D8' }}
                  >
                    {tier[svc.key]}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <button
              onClick={onBook}
              className={`w-full py-3.5 rounded-full mono text-[11px] font-semibold tracking-widest uppercase transition-all duration-300 ${
                tier.highlight
                  ? 'btn-sapphire text-white' :'text-off-white hover:border-chrome/40'
              }`}
              style={
                tier.highlight
                  ? {}
                  : {
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(212,212,216,0.15)',
                    }
              }
            >
              Book This Service
            </button>
          </div>
        ))}
      </div>

      {/* Add-ons */}
      <div
        className="rounded-2xl p-7"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(212,212,216,0.08)' }}
      >
        <p className="mono text-[10px] tracking-widest uppercase mb-5" style={{ color: '#2D5F9A' }}>
          Add-On Services
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {addOns.map((addon) => (
            <div
              key={addon.name}
              className="flex items-center justify-between px-4 py-3 rounded-xl"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(212,212,216,0.07)' }}
            >
              <span className="text-sm" style={{ color: 'rgba(212,212,216,0.75)' }}>{addon.name}</span>
              <span className="mono text-[11px] font-semibold" style={{ color: '#2D5F9A' }}>{addon.price}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}